/**
 * Tiered Rewards Custom Element - Single Reward System
 * Shows progress toward next tier, non-cumulative rewards
 */

if (!customElements.get('tiered-rewards')) {
  class TieredRewards extends HTMLElement {
    constructor() {
      super();
      
      // Tier configuration (amounts in cents - USD base)
      this.tiers = [
        { threshold: 30000, reward: 5000, tier: 1 },
        { threshold: 50000, reward: 7500, tier: 2 },
        { threshold: 75000, reward: 10000, tier: 3 },
        { threshold: 300000, reward: 50000, tier: 4 }
      ];
      
      this.congratsTimeout = null;
    }

    connectedCallback() {
      this.cartTotal = parseInt(this.dataset.cartTotal, 10) || 0;
      this.currencyRate = parseFloat(this.dataset.currencyRate) || 1;
      
      // Get references to DOM elements
      this.congratsElement = this.querySelector('.tiered-rewards__congrats');
      this.congratsText = this.querySelector('.tiered-rewards__congrats-text');
      this.messageElement = this.querySelector('.tiered-rewards__message');
      this.progressFill = this.querySelector('.tiered-rewards__progress-fill');
      this.tierElements = this.querySelectorAll('.tiered-rewards__tier');
      
      // Initialize the component
      this.initialize();
      
      // Listen for cart updates
      this.setupEventListeners();
    }

    disconnectedCallback() {
      if (this.congratsTimeout) {
        clearTimeout(this.congratsTimeout);
      }
    }

    initialize() {
      // Check if a new tier was just unlocked
      this.checkForNewTierUnlock();
      
      // Update all display elements
      this.updateDisplay();
      
      // Force update after a short delay to ensure DOM is ready
      setTimeout(() => {
        this.updateDisplay();
      }, 100);
    }

    setupEventListeners() {
      // Listen for cart updates from Shopify
      document.addEventListener('cart:updated', (event) => {
        if (event.detail && event.detail.cart) {
          this.handleCartUpdate(event.detail.cart.total_price);
        }
      });
      
      // Also listen for custom cart drawer updates
      document.addEventListener('cart-drawer:updated', (event) => {
        if (event.detail && event.detail.total) {
          this.handleCartUpdate(event.detail.total);
        }
      });
    }

    handleCartUpdate(newTotal) {
      const oldTotal = this.cartTotal;
      this.cartTotal = newTotal;
      
      // Update data attribute
      this.dataset.cartTotal = newTotal;
      
      // Check for tier unlock
      const oldTierIndex = this.getCurrentTierIndex(oldTotal);
      const newTierIndex = this.getCurrentTierIndex(newTotal);
      
      if (newTierIndex > oldTierIndex && newTierIndex >= 0) {
        this.showCongratsMessage(newTierIndex);
      }
      
      // Update all UI elements
      this.updateDisplay();
      
      // Store in session storage
      this.storeCartTotal(newTotal);
    }

    getCurrentTierIndex(total = this.cartTotal) {
      // Returns index of highest tier reached (-1 if none)
      for (let i = this.tiers.length - 1; i >= 0; i--) {
        if (total >= this.tiers[i].threshold * this.currencyRate) {
          return i;
        }
      }
      return -1; // No tier reached yet
    }

    getNextTierIndex() {
      // Returns index of next tier to reach (-1 if all complete)
      const currentIndex = this.getCurrentTierIndex();
      if (currentIndex < this.tiers.length - 1) {
        return currentIndex + 1;
      }
      return -1; // All tiers complete
    }

    updateDisplay() {
      const currentTierIndex = this.getCurrentTierIndex();
      const nextTierIndex = this.getNextTierIndex();
      
      // Update message
      this.updateMessage(currentTierIndex, nextTierIndex);
      
      // Update tier states (completed, current, locked)
      this.updateTierStates(currentTierIndex);
      
      // Update progress bar (only for current segment)
      this.updateProgressBar(currentTierIndex, nextTierIndex);
    }

    updateMessage(currentIndex, nextIndex) {
      if (!this.messageElement) return;
      
      let message = '';
      
      if (nextIndex === -1) {
        // All tiers complete
        const maxReward = this.formatMoney(this.tiers[3].reward);
        message = `<strong>Gift Card:</strong> ${maxReward} 🎉`;
      } else if (currentIndex >= 0) {
        // Has earned gift card, working toward next
        const currentReward = this.formatMoney(this.tiers[currentIndex].reward);
        const nextReward = this.formatMoney(this.tiers[nextIndex].reward);
        const remaining = (this.tiers[nextIndex].threshold * this.currencyRate) - this.cartTotal;
        const remainingFormatted = this.formatMoney(remaining);
        message = `<strong>Gift Card:</strong> ${currentReward} • ${remainingFormatted} <strong>more</strong> for ${nextReward}`;
      } else {
        // No tier reached yet
        const nextReward = this.formatMoney(this.tiers[0].reward);
        const remaining = (this.tiers[0].threshold * this.currencyRate) - this.cartTotal;
        const remainingFormatted = this.formatMoney(remaining);
        message = `${remainingFormatted} <strong>more</strong> for ${nextReward} gift card`;
      }
      
      this.messageElement.innerHTML = message;
    }

    updateTierStates(currentIndex) {
      const nextIndex = this.getNextTierIndex();
      
      this.tierElements.forEach((el, index) => {
        el.classList.remove('tiered-rewards__tier--completed', 
                            'tiered-rewards__tier--next',
                            'tiered-rewards__tier--locked');
        
        const iconElement = el.querySelector('.tier-icon');
        
        if (index <= currentIndex) {
          // All completed/passed tiers (including current) - blue checkmark, dark red text
          el.classList.add('tiered-rewards__tier--completed');
          if (iconElement) {
            iconElement.innerHTML = `
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="12" fill="#004a88"/>
                <path d="M7 12L10.5 15.5L17 9" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            `;
          }
        } else if (index === nextIndex) {
          // Next tier (actively working toward) - bright RED exclamation, not faded
          el.classList.add('tiered-rewards__tier--next');
          if (iconElement) {
            iconElement.innerHTML = `
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="11" stroke="#ee3b45" stroke-width="2" fill="none"/>
                <text x="12" y="17" text-anchor="middle" fill="#ee3b45" font-size="16" font-weight="bold">!</text>
              </svg>
            `;
          }
        } else {
          // Locked tiers (future) - gray and faded
          el.classList.add('tiered-rewards__tier--locked');
          if (iconElement) {
            iconElement.innerHTML = `
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="11" stroke="#999" stroke-width="2" fill="none"/>
                <text x="12" y="17" text-anchor="middle" fill="#999" font-size="16" font-weight="bold">!</text>
              </svg>
            `;
          }
        }
      });
    }

    updateProgressBar(currentIndex, nextIndex) {
      if (!this.progressFill) return;
      
      if (nextIndex === -1) {
        // All tiers complete - 100%
        this.progressFill.style.transform = 'translateX(0)';
        return;
      }
      
      // Calculate progress within CURRENT tier segment only
      // Bar spans full width, fill shows 0-100% progress in active tier
      const segmentStart = currentIndex >= 0 ? this.tiers[currentIndex].threshold * this.currencyRate : 0;
      const segmentEnd = this.tiers[nextIndex].threshold * this.currencyRate;
      const segmentProgress = this.cartTotal - segmentStart;
      const segmentRange = segmentEnd - segmentStart;
      const percentage = (segmentProgress / segmentRange) * 100;
      
      // Fill from 0-100% for each tier (resets when crossing tier)
      const clamped = Math.min(Math.max(percentage, 0), 100);
      this.progressFill.style.transform = `translateX(${clamped - 100}%)`;
    }

    checkForNewTierUnlock() {
      const previousTotal = this.getPreviousCartTotal();
      
      if (previousTotal !== null) {
        const oldTierIndex = this.getCurrentTierIndex(previousTotal);
        const newTierIndex = this.getCurrentTierIndex(this.cartTotal);
        
        if (newTierIndex > oldTierIndex && newTierIndex >= 0) {
          this.showCongratsMessage(newTierIndex);
        }
      }
      
      // Store current total
      this.storeCartTotal(this.cartTotal);
    }

    showCongratsMessage(tierIndex) {
      const tier = this.tiers[tierIndex];
      if (!tier || !this.congratsElement || !this.congratsText) return;
      
      // Format the reward amount in the current currency
      const rewardAmount = this.formatMoney(tier.reward);
      
      // Show message - "earned" per Curtis's feedback
      const message = `Congrats! You earned a ${rewardAmount} gift card! 🎉`;
      
      this.congratsText.textContent = message;
      this.congratsElement.classList.remove('tw:hidden', 'slide-out');
      this.congratsElement.style.display = 'block';
      
      // Auto-hide after 4 seconds with slide-out animation
      if (this.congratsTimeout) {
        clearTimeout(this.congratsTimeout);
      }
      
      this.congratsTimeout = setTimeout(() => {
        if (this.congratsElement) {
          // Add slide-out class for exit animation
          this.congratsElement.classList.add('slide-out');
          
          // Hide completely after animation finishes
          setTimeout(() => {
            if (this.congratsElement) {
              this.congratsElement.classList.add('tw:hidden');
              this.congratsElement.style.display = 'none';
            }
          }, 400); // Match animation duration
        }
      }, 4000);
    }

    formatMoney(cents) {
      if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
        const format = window.theme?.settings?.money_with_currency_format || '${{amount}}';
        return Shopify.formatMoney(cents, format);
      }
      
      // Fallback formatting
      const dollars = (cents / 100).toFixed(2);
      return `$${dollars}`;
    }

    storeCartTotal(total) {
      try {
        sessionStorage.setItem('tiered_rewards_last_total', total.toString());
      } catch (e) {
        console.warn('Session storage not available for tiered rewards');
      }
    }

    getPreviousCartTotal() {
      try {
        const stored = sessionStorage.getItem('tiered_rewards_last_total');
        return stored ? parseInt(stored, 10) : null;
      } catch (e) {
        return null;
      }
    }
  }

  customElements.define('tiered-rewards', TieredRewards);
}
